import { Container, Sprite, TilingSprite } from 'pixi.js';
import { tileToScreen } from '../sim/projection';
import type { GameMap } from '../sim/map';
import type { LandTextures } from './landArt';
import type { TileBounds } from './viewportCull';

/**
 * One placed land sprite plus the tile it draws, returned alongside the container
 * so the renderer can viewport-cull terrain: ~33k ground sprites cost real GPU
 * time even though they never sort, so the renderer toggles each one's `visible`
 * by the camera window, and needs each sprite's tile coords to do so.
 */
export interface TerrainTileInfo {
  x: number;
  y: number;
  /**
   * The tile's land elevation (`GameMap.landZ`), already folded into the
   * tile's screen placement (terraced rendering, PRD #536 Slice C). Kept here
   * so the renderer can derive its height-aware cull margin and picking lookup
   * without re-reading the map. Always 0 on a flat map.
   */
  z: number;
}

/**
 * A terrain tile that owns its own Sprite (the classic per-tile ground). The
 * sloped-mesh path (PRD #536 Slice E) has no per-tile display object - its
 * ground is chunked meshes - so it feeds the renderer bare
 * {@link TerrainTileInfo}s and culls per chunk instead.
 */
export interface TerrainTile extends TerrainTileInfo {
  sprite: Sprite;
}

/** The built ground layer: the single container plus its per-tile sprites.
 * For an open-plain map (PRD #387) `tiles` is empty and `procedural` carries
 * the windowed ground the renderer drives from its cull bounds instead. */
export interface Terrain {
  container: Container;
  tiles: TerrainTileInfo[];
  procedural?: ProceduralTerrain;
  windowed?: WindowedTerrain;
}

function intersectBounds(a: TileBounds, b: TileBounds): TileBounds | undefined {
  const intersection = {
    minX: Math.max(a.minX, b.minX),
    maxX: Math.min(a.maxX, b.maxX),
    minY: Math.max(a.minY, b.minY),
    maxY: Math.min(a.maxY, b.maxY),
  };
  return intersection.minX <= intersection.maxX && intersection.minY <= intersection.maxY
    ? intersection
    : undefined;
}

function forEachTileOutside(
  bounds: TileBounds,
  retained: TileBounds | undefined,
  visit: (x: number, y: number) => void,
): void {
  if (!retained) {
    for (let y = bounds.minY; y <= bounds.maxY; y += 1) {
      for (let x = bounds.minX; x <= bounds.maxX; x += 1) visit(x, y);
    }
    return;
  }
  for (let y = bounds.minY; y < retained.minY; y += 1) {
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) visit(x, y);
  }
  for (let y = retained.minY; y <= retained.maxY; y += 1) {
    for (let x = bounds.minX; x < retained.minX; x += 1) visit(x, y);
    for (let x = retained.maxX + 1; x <= bounds.maxX; x += 1) visit(x, y);
  }
  for (let y = retained.maxY + 1; y <= bounds.maxY; y += 1) {
    for (let x = bounds.minX; x <= bounds.maxX; x += 1) visit(x, y);
  }
}

/**
 * The windowed procedural ground of an **open-plain** map (PRD #387 Slice C).
 * The renderer calls {@link update} only when its cull window changes, so a
 * still camera costs nothing.
 *
 * The normal path is two retained repeating planes for the two staggered
 * halves of the isometric diamond lattice.
 * Ordinary camera movement changes only their rectangular cover window.
 * A multi-tile palette retains deterministic {@link GameMap.tileNameAt} art
 * through a pooled edge-diffed sprite path.
 * That path is also the safe degrade when the base texture is unavailable.
 */
export class ProceduralTerrain {
  readonly container = new Container();
  private readonly repeats: readonly [TilingSprite, TilingSprite] | undefined;
  private logicalActiveCount = 0;
  private readonly pool: Sprite[] = [];
  private readonly active = new Map<string, Sprite>();
  private readonly available: Sprite[] = [];
  private bounds: TileBounds | undefined;

  constructor(
    private readonly map: GameMap,
    private readonly textures: LandTextures,
  ) {
    const palette = map.toData().palette;
    const texture = textures.get(palette[0]!);
    if (palette.length === 1 && texture) {
      this.repeats = [
        new TilingSprite({ texture, width: 1, height: 1 }),
        new TilingSprite({ texture, width: 1, height: 1 }),
      ];
      this.container.addChild(...this.repeats);
    }
  }

  get activeCount(): number {
    return this.logicalActiveCount;
  }

  /** Update the pooled window to cover `bounds` (no bounds - no camera yet - blanks it). */
  update(bounds: TileBounds | undefined): void {
    if (this.repeats) {
      this.updateRepeats(bounds);
      return;
    }
    this.updateSprites(bounds);
  }

  private updateRepeats(bounds: TileBounds | undefined): void {
    if (!bounds) {
      for (const repeat of this.repeats!) repeat.visible = false;
      this.logicalActiveCount = 0;
      return;
    }
    const width = bounds.maxX - bounds.minX + 1;
    const height = bounds.maxY - bounds.minY + 1;
    const left = tileToScreen(bounds.minX, bounds.maxY).x - 22;
    const right = tileToScreen(bounds.maxX, bounds.minY).x + 22;
    const top = tileToScreen(bounds.minX, bounds.minY).y;
    const bottom = tileToScreen(bounds.maxX, bounds.maxY).y + 44;
    const phases = [
      { x: -22, y: 0 },
      { x: 0, y: 22 },
    ];
    for (let index = 0; index < this.repeats!.length; index += 1) {
      const repeat = this.repeats![index]!;
      const phase = phases[index]!;
      repeat.position.set(left, top);
      repeat.width = right - left;
      repeat.height = bottom - top;
      repeat.tilePosition.set(phase.x - left, phase.y - top);
      repeat.visible = true;
    }
    this.logicalActiveCount = width * height;
  }

  private updateSprites(bounds: TileBounds | undefined): void {
    if (!bounds) {
      for (const sprite of this.active.values()) {
        sprite.visible = false;
        this.available.push(sprite);
      }
      this.active.clear();
      this.bounds = undefined;
      this.logicalActiveCount = 0;
      return;
    }

    const retained = this.bounds ? intersectBounds(this.bounds, bounds) : undefined;
    if (this.bounds) {
      forEachTileOutside(this.bounds, retained, (x, y) => {
        const key = `${x},${y}`;
        const sprite = this.active.get(key);
        if (!sprite) return;
        this.active.delete(key);
        sprite.visible = false;
        this.available.push(sprite);
      });
    }

    forEachTileOutside(bounds, retained, (x, y) => {
      const name = this.map.tileNameAt(x, y);
      const texture = name !== undefined ? this.textures.get(name) : undefined;
      if (!texture) return;
      let sprite = this.available.pop();
      if (!sprite) {
        sprite = new Sprite();
        sprite.anchor.set(0.5, 0);
        this.pool.push(sprite);
        this.container.addChild(sprite);
      }
      sprite.texture = texture;
      const screen = tileToScreen(x, y);
      sprite.position.set(screen.x, screen.y);
      sprite.visible = true;
      this.active.set(`${x},${y}`, sprite);
    });
    this.bounds = { ...bounds };
    this.logicalActiveCount = this.active.size;
  }
}

/**
 * The pooled terrain presentation for a bounded map.
 *
 * Complete tile metadata remains in {@link Terrain.tiles} for height-aware
 * picking and culling budgets, but Pixi objects exist only for the current
 * camera window.
 * The renderer calls {@link update} only when that window changes, so a steady
 * camera performs no terrain placement work.
 */
export class WindowedTerrain {
  readonly container = new Container();
  private readonly pool: Sprite[] = [];

  constructor(
    private readonly map: GameMap,
    private readonly textures: LandTextures,
  ) {}

  /** Repaint the pooled window, clipped to the bounded map. */
  update(bounds: TileBounds | undefined): void {
    let used = 0;
    if (bounds) {
      const minX = Math.max(0, bounds.minX);
      const maxX = Math.min(this.map.width - 1, bounds.maxX);
      const minY = Math.max(0, bounds.minY);
      const maxY = Math.min(this.map.height - 1, bounds.maxY);
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const name = this.map.tileNameAt(x, y);
          const texture = name !== undefined ? this.textures.get(name) : undefined;
          if (!texture) continue;
          let sprite = this.pool[used];
          if (!sprite) {
            sprite = new Sprite();
            sprite.anchor.set(0.5, 0);
            this.pool.push(sprite);
            this.container.addChild(sprite);
          }
          sprite.texture = texture;
          const screen = tileToScreen(x, y, this.map.landZ(x, y));
          sprite.position.set(screen.x, screen.y);
          sprite.visible = true;
          used += 1;
        }
      }
    }
    for (let i = used; i < this.pool.length; i++) this.pool[i]!.visible = false;
  }

  get activeCount(): number {
    let count = 0;
    for (const sprite of this.pool) {
      if (sprite.visible) count += 1;
    }
    return count;
  }
}

/**
 * Build the ground layer: one 44x44 land diamond sprite per map cell, placed by
 * the UO-accurate projection. Replaces the M1 checkerboard `createFloor` with
 * real baked land art.
 *
 * A land tile sprite is the full 44x44 diamond whose top corner is the tile's
 * screen anchor, so anchor (0.5, 0) - centred horizontally, hung from the top -
 * lands the sprite exactly over the dimetric tile at `tileToScreen(x, y)`.
 *
 * Iterates row-major so tiles further back (lower x+y) are added first and
 * nearer tiles overlap them - the natural draw order for flat ground. Real depth
 * sorting against statics and the player arrives in M3.
 *
 * Returns both the `container` (still added to the world at `TERRAIN_Z` as one
 * sub-container) and the `tiles` list pairing each sprite with its tile coords,
 * so the renderer can viewport-cull the ground by toggling sprite visibility.
 */
export function createTerrain(map: GameMap, textures: LandTextures): Terrain {
  // Open plain (PRD #387): no grid to materialize - the ground is the pooled,
  // camera-windowed procedural layer the renderer drives from its cull bounds.
  if (map.isPlain) {
    const procedural = new ProceduralTerrain(map, textures);
    return { container: procedural.container, tiles: [], procedural };
  }

  const windowed = new WindowedTerrain(map, textures);
  const tiles: TerrainTileInfo[] = [];

  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const name = map.tileNameAt(x, y);
      if (name === undefined) continue;
      if (!textures.has(name)) {
        console.warn(`no baked land texture for tile "${name}" at ${x},${y} - skipped`);
        continue;
      }
      const z = map.landZ(x, y);
      tiles.push({ x, y, z });
    }
  }

  return { container: windowed.container, tiles, windowed };
}
